/**
 * PlanDropZone — Visuell sichtbare Drop-Zone im Viewer (bottom-left).
 *
 * Wird nur im Grundriss-/Schnitt-Tab angezeigt. Nimmt:
 *  - Paperless-Dokumente per Drag & Drop aus der Sidebar (Doc-Tab)
 *  - Lokale PDF-Dateien per Drag & Drop vom Desktop
 *  - Lokale Bilddateien (PNG, JPG, WebP, BMP, SVG, GIF, TIFF) per Drag & Drop
 *
 * Nach dem Drop wird die Datei als 3D-Plan-Overlay geladen.
 * Wenn bereits ein Overlay aktiv ist, werden stattdessen die Controls angezeigt.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'

declare global {
  interface Window {
    planOverlayLoad: (url: string, page?: number) => Promise<any>
    planOverlayLoadFromFile: (file: File, page?: number) => Promise<any>
    planOverlayUpdate: (opts: Record<string, number>) => void
    planOverlayRemove: () => void
    planOverlayConfirm: () => void
    planOverlayStartRegistration: (numPoints?: number) => void
    planOverlayAbortRegistration: () => void
    planOverlayState: {
      active: boolean; confirmed: boolean
      opacity: number; scale: number; rotation: number
      offsetX: number; offsetY: number; fileName: string | null
      regPhase: string; regPlanCount: number; regCloudCount: number; regNeeded: number
    } | null
    activeSection: any
    activeFloorplan: any
    createSceneFromViewer: (data: any) => any
    saveCameraCmd: () => void
  }
}

/* ─── Styles ─── */
const S = {
  panel: {
    background: '#e8e8e8',
    borderRadius: '10px',
    padding: '3px',
    boxShadow: '0 1px 8px rgba(0,0,0,0.08)',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', Roboto, sans-serif",
  },
  dropzone: {
    background: 'transparent',
    borderRadius: '8px',
    border: '1.5px dashed rgba(0,0,0,0.15)',
    padding: '12px 16px',
    cursor: 'default',
  },
  dropzoneHover: {
    background: 'rgba(59,130,246,0.06)',
    border: '1.5px solid rgba(59,130,246,0.4)',
  },
  inner: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '0',
    padding: '6px 8px 4px',
  },
  row: {
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: '8px',
    height: '28px',
  },
  label: {
    fontSize: '11px',
    fontWeight: 400 as const,
    color: '#666',
    width: '56px',
    flexShrink: 0,
    letterSpacing: '-0.01em',
  },
  btn: {
    height: '32px',
    padding: '0 12px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    color: '#999',
    fontSize: '11px',
    fontWeight: 400 as const,
    cursor: 'pointer',
    transition: 'background 0.15s ease, color 0.15s ease',
    whiteSpace: 'nowrap' as const,
    fontFamily: 'inherit',
    letterSpacing: '-0.01em',
  },
  btnActive: {
    background: '#333',
    color: '#fff',
    fontWeight: 600 as const,
    boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
  },
  btnPrimary: {
    background: '#333',
    color: '#fff',
    fontWeight: 600 as const,
    boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
  },
  sep: {
    width: '1px',
    height: '16px',
    background: 'rgba(0,0,0,0.1)',
    flexShrink: 0,
  },
} as const

export function PlanDropZone() {
  const [isInView, setIsInView] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fileName, setFileName] = useState<string | null>(null)

  // Controls state
  const [opacity, setOpacity] = useState(0.6)
  const [scale, setScale] = useState(1.0)
  const [rotation, setRotation] = useState(0)
  const [offsetX, setOffsetX] = useState(0)
  const [offsetY, setOffsetY] = useState(0)

  // Registration state
  const [regPhase, setRegPhase] = useState('idle')
  const [regPlanCount, setRegPlanCount] = useState(0)
  const [regCloudCount, setRegCloudCount] = useState(0)
  const [regNeeded, setRegNeeded] = useState(2)

  const sliderRef = useRef<HTMLInputElement>(null)

  // Sync with viewer state
  useEffect(() => {
    const check = () => {
      // Only show in Prototyp Datenlayer mode + Grundriss tab
      const isDevDataLayers = (window as any)._devModeActiveId === 'data-layers'
      const isGrundrissTab = (window as any)._currentViewTab === 'grundriss'
      const inView = isDevDataLayers && isGrundrissTab && !!(window.activeFloorplan)
      setIsInView(inView)
      const st = window.planOverlayState
      setIsActive(!!st?.active)
      if (st?.active) {
        if (st.fileName) setFileName(st.fileName)
        setConfirmed(!!st.confirmed)
        setRegPhase(st.regPhase || 'idle')
        setRegPlanCount(st.regPlanCount || 0)
        setRegCloudCount(st.regCloudCount || 0)
        setRegNeeded(st.regNeeded || 2)
        setOpacity(st.opacity)
        setScale(st.scale)
        setRotation(st.rotation)
        setOffsetX(st.offsetX)
        setOffsetY(st.offsetY)
      }
    }
    check()
    const iv = setInterval(check, 300)
    return () => clearInterval(iv)
  }, [])

  // Canvas-Handle → React sync
  useEffect(() => {
    (window as any)._planOverlayOnChange = (opts: any) => {
      setOpacity(opts.opacity)
      setScale(opts.scale)
      setRotation(opts.rotation)
      setOffsetX(opts.offsetX)
      setOffsetY(opts.offsetY)
    }
    return () => { (window as any)._planOverlayOnChange = null }
  }, [])

  // Keep slider in sync with external opacity changes
  useEffect(() => {
    if (sliderRef.current && document.activeElement !== sliderRef.current)
      sliderRef.current.value = String(opacity)
  }, [opacity])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)

    const plData = e.dataTransfer.getData('application/x-paperless-doc')
    if (plData) {
      try {
        const doc = JSON.parse(plData)
        setLoading(true)
        setFileName(doc.title || doc.fileName)
        const result = await window.planOverlayLoad(doc.downloadUrl, 1)
        if (result) {
          setIsActive(true); setConfirmed(false)
          setOpacity(0.6); setScale(1.0); setRotation(0); setOffsetX(0); setOffsetY(0)
        }
      } catch { /**/ }
      setLoading(false)
      return
    }

    const file = e.dataTransfer.files[0]
    if (file) {
      const ext = file.name.toLowerCase().replace(/^.*\./, '')
      const supported = ['pdf', 'png', 'jpg', 'jpeg', 'webp', 'bmp', 'svg', 'gif', 'tiff', 'tif']
      if (supported.includes(ext) || file.type.startsWith('image/')) {
        setLoading(true)
        setFileName(file.name)
        try {
          const result = await window.planOverlayLoadFromFile(file, 1)
          if (result) {
            setIsActive(true); setConfirmed(false)
            setOpacity(0.6); setScale(1.0); setRotation(0); setOffsetX(0); setOffsetY(0)
          }
        } catch { /**/ }
        setLoading(false)
      }
    }
  }, [])

  const handleRemove = useCallback(() => {
    window.planOverlayRemove()
    setIsActive(false)
    setConfirmed(false)
    setFileName(null)
  }, [])

  const handleSaveScene = useCallback(() => {
    // Call the global saveCameraCmd which creates a Scene via React bridge
    if ((window as any).saveCameraCmd) {
      ;(window as any).saveCameraCmd()
    } else if (window.createSceneFromViewer) {
      // Fallback: directly create scene from viewer state
      const v = (window as any).viewer?.scene?.view
      if (!v) return
      const camera = {
        position: { x: v.position.x, y: v.position.y, z: v.position.z },
        target: (() => { const p = v.getPivot(); return { x: p.x, y: p.y, z: p.z } })(),
      }
      const cv = (window as any).currentClipVolume
      const clipBox = cv ? {
        position: [cv.position.x, cv.position.y, cv.position.z],
        scale: [cv.scale.x, cv.scale.y, cv.scale.z],
        rotation: [cv.rotation.x, cv.rotation.y, cv.rotation.z],
      } : null
      window.createSceneFromViewer({ camera, clipBox, activeCloudIds: [] })
    }
  }, [])

  const push = useCallback((opts: Record<string, number>) => {
    window.planOverlayUpdate(opts)
  }, [])

  if (!isInView) return null

  const portalRoot = document.getElementById('plan-dropzone-root')
  if (!portalRoot) return null

  return createPortal(
    <div
      data-component="PlanDropZone"
      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDragOver(true) }}
      onDragEnter={e => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={e => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
        if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
          setDragOver(false)
        }
      }}
      onDrop={handleDrop}
      style={{
        position: 'fixed',
        right: '16px',
        bottom: '72px',
        zIndex: 10400,
        pointerEvents: 'auto',
        transition: 'all 0.2s ease',
        ...S.panel,
        ...(isActive ? { width: '240px' } : { width: '200px' }),
      }}
    >
      {/* ── Drop Zone (Idle) ── */}
      {!isActive && !loading && (
        <div
          data-section="plan-drop-idle"
          style={{
            ...S.dropzone,
            ...(dragOver ? S.dropzoneHover : {}),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            textAlign: 'center',
            transition: 'all 0.15s ease',
          }}
        >
          <svg
            width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke={dragOver ? '#3b82f6' : '#999'}
            strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
            style={{ transition: 'stroke 0.15s' }}
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="12" y1="18" x2="12" y2="12"/>
            <line x1="9" y1="15" x2="12" y2="12"/>
            <line x1="15" y1="15" x2="12" y2="12"/>
          </svg>
          <div style={{
            fontSize: '11px', fontWeight: 500,
            color: dragOver ? '#3b82f6' : '#666',
            transition: 'color 0.15s',
          }}>
            {dragOver ? 'Loslassen zum Laden' : 'Plan hierhin ziehen'}
          </div>
          <div style={{ fontSize: '9px', color: '#aaa', lineHeight: 1.3 }}>
            PDF oder Bild — aus Sidebar oder vom Desktop
          </div>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div data-section="plan-drop-loading" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '12px', color: '#666', fontSize: '11px',
        }}>
          Lade Plan…
        </div>
      )}

      {/* ── Active Controls ── */}
      {isActive && !loading && (
        <div data-section="plan-overlay-controls" style={S.inner}>
          {/* Header: Filename + Close */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            height: '28px', marginBottom: '2px',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            <span style={{
              flex: 1, fontSize: '11px', fontWeight: 600, color: '#333',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              letterSpacing: '-0.01em',
            }}>
              {fileName ?? 'Plan'}
            </span>
            <button
              data-element="btn-remove-plan"
              onClick={handleRemove}
              title="Plan entfernen"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '20px', height: '20px', borderRadius: '6px',
                border: 'none', background: 'transparent',
                color: '#999', cursor: 'pointer', flexShrink: 0,
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(220,38,38,0.08)'
                e.currentTarget.style.color = '#dc2626'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent'
                e.currentTarget.style.color = '#999'
              }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* Deckkraft Slider */}
          <div style={S.row}>
            <span style={S.label}>Deckkraft</span>
            <input
              ref={sliderRef}
              type="range" min={0} max={1} step={0.05}
              defaultValue={String(opacity)}
              onChange={e => {
                const v = parseFloat(e.target.value)
                setOpacity(v)
                push({ opacity: v })
              }}
              style={{
                flex: 1, height: '3px', cursor: 'pointer',
                accentColor: '#333',
              }}
            />
          </div>

          {/* Separator */}
          <div style={{ height: '1px', background: 'rgba(0,0,0,0.08)', margin: '4px 0' }} />

          {/* Registration status (when active) */}
          {!confirmed && regPhase !== 'idle' && (
            <div style={{
              padding: '6px 8px',
              borderRadius: '8px',
              background: regPhase === 'planPicking' ? 'rgba(231,76,60,0.08)' : 'rgba(59,130,246,0.08)',
              marginBottom: '4px',
            }}>
              <div style={{
                fontSize: '10px', fontWeight: 600,
                color: regPhase === 'planPicking' ? '#dc2626' : '#2563eb',
                marginBottom: '4px',
              }}>
                {regPhase === 'planPicking'
                  ? `Plan-Punkt ${regPlanCount + 1}/${regNeeded} klicken`
                  : `Wolke-Punkt ${regCloudCount + 1}/${regNeeded} klicken`
                }
              </div>
              {/* Progress dots */}
              <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                {Array.from({ length: regNeeded }).map((_, i) => {
                  const planDone = i < regPlanCount
                  const cloudDone = i < regCloudCount
                  return (
                    <div key={i} style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                      <div style={{
                        width: '7px', height: '7px', borderRadius: '50%',
                        background: planDone ? '#dc2626' : 'rgba(220,38,38,0.15)',
                        border: '1px solid rgba(220,38,38,0.3)',
                      }} />
                      <div style={{
                        width: '7px', height: '7px', borderRadius: '50%',
                        background: cloudDone ? '#2563eb' : 'rgba(59,130,246,0.15)',
                        border: '1px solid rgba(59,130,246,0.3)',
                      }} />
                    </div>
                  )
                })}
              </div>
              <button
                onClick={() => { window.planOverlayAbortRegistration(); setRegPhase('idle') }}
                style={{
                  ...S.btn,
                  height: '26px',
                  width: '100%',
                  fontSize: '10px',
                  background: 'rgba(0,0,0,0.04)',
                  color: '#999',
                  borderRadius: '6px',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.08)'; e.currentTarget.style.color = '#666' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; e.currentTarget.style.color = '#999' }}
              >
                Abbrechen
              </button>
            </div>
          )}

          {/* Action Buttons — pill-style like dock */}
          {!confirmed && regPhase === 'idle' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
            }}>
              <button
                data-element="btn-confirm-plan"
                onClick={() => { window.planOverlayConfirm(); setConfirmed(true) }}
                title="Plan bestätigen — Punktwolke + Plan überlagert anzeigen"
                style={{ ...S.btn, ...S.btnPrimary }}
              >
                Bestätigen
              </button>
              <div style={S.sep} />
              <button
                data-element="btn-register-plan"
                onClick={() => window.planOverlayStartRegistration(2)}
                title="Linien-Abgleich: Plan ↔ Punktwolke"
                style={S.btn}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; e.currentTarget.style.color = '#555' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#999' }}
              >
                Abgleich
              </button>
              <div style={S.sep} />
              <button
                data-element="btn-remove-plan-alt"
                onClick={handleRemove}
                title="Plan entfernen"
                style={{ ...S.btn, padding: '0 8px' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.06)'; e.currentTarget.style.color = '#dc2626' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#999' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
              </button>
            </div>
          )}

          {/* Confirmed state: Plan + Punktwolke überlagert */}
          {confirmed && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
            }}>
              <button
                data-element="btn-edit-plan"
                onClick={() => {
                  // Zurück in Adjustment-Phase: Punktwolke ausblenden, Handles anmelden
                  ;(window as any).planOverlayUnconfirm?.()
                  setConfirmed(false)
                }}
                title="Plan erneut positionieren"
                style={S.btn}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; e.currentTarget.style.color = '#555' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#999' }}
              >
                Bearbeiten
              </button>
              <div style={S.sep} />
              <button
                data-element="btn-save-scene"
                onClick={handleSaveScene}
                title="Aktuellen Stand als Scene speichern"
                style={S.btn}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; e.currentTarget.style.color = '#555' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#999' }}
              >
                Scene speichern
              </button>
              <div style={S.sep} />
              <button
                data-element="btn-remove-plan-alt"
                onClick={handleRemove}
                title="Plan entfernen"
                style={{ ...S.btn, padding: '0 8px' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.06)'; e.currentTarget.style.color = '#dc2626' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#999' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
    </div>,
    portalRoot,
  )
}
